import type { FlightCardData, FlightJourneySegment } from '@massilia/queries'

import type { LuggageFilterValue } from '@/lib/schemas'

import type { DurationRange } from './duration-filter'
import type { PriceRange } from './price-filter'
import type { SegmentTimeFilter } from './time-filter'

export interface FlightFiltersState {
  durationRange?: DurationRange
  excludedAirlines: string[]
  luggage: LuggageFilterValue
  priceRange?: PriceRange
  stops: 'all' | 'direct' | 'one_stop_max' | 'two_stops_max'
  timeFilters: SegmentTimeFilter[]
}

// Helper function to filter offers based on filter state
export function applyFilters(
  offers: FlightCardData[],
  filters: FlightFiltersState
): FlightCardData[] {
  return offers.filter((offer) => {
    // Filter by price
    if (filters.priceRange) {
      const price = parseFloat(offer.pricing.total.amount)
      if (price < filters.priceRange.min || price > filters.priceRange.max) {
        return false
      }
    }

    // Filter by duration
    if (filters.durationRange) {
      // Calculate total duration across all segments
      let totalDuration = offer.outbound.totalDuration || 0
      if (offer.return) {
        totalDuration += offer.return.totalDuration || 0
      }
      if (offer.segments) {
        totalDuration += offer.segments.reduce(
          (sum, segment) => sum + (segment.totalDuration || 0),
          0
        )
      }

      if (totalDuration < filters.durationRange.min || totalDuration > filters.durationRange.max) {
        return false
      }
    }

    // Filter by airline (blacklist)
    if (filters.excludedAirlines.includes(offer.airlineCode)) {
      return false
    }

    // Filter by stops - check all segments including multi-destination
    let maxStops = Math.max(offer.outbound.stops, offer.return?.stops ?? 0)
    if (offer.segments && offer.segments.length > 0) {
      const segmentMaxStops = Math.max(...offer.segments.map((s) => s.stops))
      maxStops = Math.max(maxStops, segmentMaxStops)
    }

    switch (filters.stops) {
      case 'direct':
        if (maxStops > 0) return false
        break
      case 'one_stop_max':
        if (maxStops > 1) return false
        break
      case 'two_stops_max':
        if (maxStops > 2) return false
        break
      case 'all':
      default:
        break
    }

    // Filter by luggage (minimum checked bags)
    const minCheckedBags = parseInt(filters.luggage, 10)
    if (offer.minCheckedBags < minCheckedBags) {
      return false
    }

    // Filter by time slots
    if (filters.timeFilters.length > 0) {
      const allSegments = getAllSegments(offer)

      for (const timeFilter of filters.timeFilters) {
        const segment = allSegments[timeFilter.segmentIndex]
        if (!segment) continue

        const depHour = getHourFromISOString(segment.departureTime)
        const arrHour = getHourFromISOString(segment.arrivalTime)

        // Helper function to check if hour is in any selected time slot
        const isHourInSlots = (hour: number, slots: string[]): boolean => {
          return slots.some((slot) => {
            switch (slot) {
              case '0-6':
                return hour >= 0 && hour < 6
              case '6-12':
                return hour >= 6 && hour < 12
              case '12-18':
                return hour >= 12 && hour < 18
              case '18-24':
                return hour >= 18 && hour <= 24
              default:
                return false
            }
          })
        }

        // Check if departure time is in selected slots (only if slots are specified)
        if (
          timeFilter.departureTimeSlots.length > 0 &&
          !isHourInSlots(depHour, timeFilter.departureTimeSlots)
        ) {
          return false
        }

        // Check if arrival time is in selected slots (only if slots are specified)
        if (
          timeFilter.arrivalTimeSlots.length > 0 &&
          !isHourInSlots(arrHour, timeFilter.arrivalTimeSlots)
        ) {
          return false
        }
      }
    }

    return true
  })
}

// Format segment label
export function formatSegmentLabel(segment: FlightJourneySegment): string {
  return `${segment.departureCity} → ${segment.arrivalCity}`
}

// Helper function to extract all segments from flight offers
export function getAllSegmentsFromOffers(offers: FlightCardData[]): FlightJourneySegment[] {
  if (offers.length === 0) return []

  const offer = offers[0] // Assume all offers have the same trip structure

  if (offer.segments && offer.segments.length > 0) {
    // Multi-destination: use the segments array
    return offer.segments
  } else if (offer.return) {
    // Round-trip: outbound and return
    return [offer.outbound, offer.return]
  } else {
    // One-way: outbound only
    return [offer.outbound]
  }
}

// Helper function to get duration bounds from offers
export function getDurationBounds(offers: FlightCardData[]): DurationRange {
  if (offers.length === 0) {
    return { max: 24 * 60, min: 0 } // 24 hours in minutes
  }

  const durations = offers.map((offer) => {
    // Calculate total duration across all segments
    let totalDuration = offer.outbound.totalDuration || 0
    if (offer.return) {
      totalDuration += offer.return.totalDuration || 0
    }
    if (offer.segments) {
      totalDuration += offer.segments.reduce(
        (sum, segment) => sum + (segment.totalDuration || 0),
        0
      )
    }
    return totalDuration
  })

  return {
    max: Math.ceil(Math.max(...durations) / 30) * 30, // Round up to nearest 30 minutes
    min: Math.floor(Math.min(...durations) / 30) * 30, // Round down to nearest 30 minutes
  }
}

// Convert ISO time string to hour number (0-24)
export function getHourFromISOString(isoString: string): number {
  const date = new Date(isoString)
  return date.getHours() + date.getMinutes() / 60
}

// Helper function to get price bounds from offers
export function getPriceBounds(offers: FlightCardData[]): PriceRange {
  if (offers.length === 0) {
    return { max: 1000, min: 0 }
  }

  const prices = offers.map((offer) => parseFloat(offer.pricing.total.amount))
  return {
    max: Math.ceil(Math.max(...prices)),
    min: Math.floor(Math.min(...prices)),
  }
}

// Helper function to get a specific segment from an offer
export function getSegmentFromOffer(
  offer: FlightCardData,
  segmentIndex: number
): FlightJourneySegment | null {
  const allSegments = getAllSegmentsFromOffers([offer])
  return allSegments[segmentIndex] || null
}

// Helper function to get all segments from a flight offer
function getAllSegments(offer: FlightCardData): FlightJourneySegment[] {
  const segments = [offer.outbound]
  if (offer.return) segments.push(offer.return)
  if (offer.segments) segments.push(...offer.segments)
  return segments
}
